import React, { RefObject, useRef } from "react";
import Draggable, { DraggableData } from "react-draggable";
import useDashboardStore from "store/dashboard-store";
import useScadaStore from "store/scada-store";
import useUpdateScadaConfig from "hooks/useUpdateScadaConfig";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfo } from "models/dataset-models";
import { Instant } from "@js-joda/core";
import { ScadaFreeTextTagTable, ScadaTagsProps } from "../scadaTypes";
import ScadaTag from "./ScadaTag";
import { calculateLabelTransform } from "../utils/scadaUtils";

type ScadaFreeTextTagTablesProps = ScadaTagsProps & {
  scadaId: string | undefined;
  refTime: Instant | undefined;
  freeTextTagTables: ScadaFreeTextTagTable[];
};

const ScadaFreeTextTagTables: React.FC<ScadaFreeTextTagTablesProps> = props => {
  const { scadaId, parent, gridSize, freeTextTagTables, refreshRate, fontSizeScale } = props;
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const scadaStore = useScadaStore();
  const updateScadaConfig = useUpdateScadaConfig();

  const refs = useRef<Record<string, RefObject<HTMLDivElement>>>({});
  freeTextTagTables.forEach(table => {
    if (!refs.current[table.id]) {
      refs.current[table.id] = React.createRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
    }
  });

  const allTagIdsWithDiffs = [
    ...new Set(
      freeTextTagTables
        .flatMap(t => t.rows)
        .filter(r => r.tag.diffOptions?.isIndicatorVisible)
        .map(r => r.tag.identifier)
    ),
  ];

  const allTagIdsWithoutDiffs = [
    ...new Set(
      freeTextTagTables
        .flatMap(t => t.rows)
        .filter(r => !r.tag.diffOptions?.isIndicatorVisible)
        .map(r => r.tag.identifier)
    ),
  ];

  const queriesDataWithoutDiffs = useGetRecentData(allTagIdsWithoutDiffs, {
    refTime: props.refTime?.toString(),
    options: { refetchInterval: refreshRate ? refreshRate * 1000 : 0 },
  });

  const queriesDataWithDiffs = useGetRecentData(allTagIdsWithDiffs, {
    count: 2,
    refTime: props.refTime?.toString(),
    options: { refetchInterval: refreshRate ? refreshRate * 1000 : 0 },
  });

  const mergedData = [...(queriesDataWithoutDiffs.data ?? []), ...(queriesDataWithDiffs.data ?? [])];

  const dataMap: Map<string, DatasetDataInfo> = new Map(
    mergedData.map(q => [q.identifier, { ...q, loadedTime: Instant.now() }] as [string, DatasetDataInfo])
  );

  const getLabelXY = (ref: HTMLElement, dragData: DraggableData) => {
    const { left, top } = ref.getBoundingClientRect();

    if (parent.width && parent.height) {
      const relX = left - parent.left + dragData.deltaX;
      const relY = top - parent.top + dragData.deltaY;
      let pX = Math.round((relX / parent.width) * 1000) / 10;
      let pY = Math.round((relY / parent.height) * 1000) / 10;
      pX = Math.max(0, pX);
      pY = Math.max(0, pY);
      return { x: pX, y: pY };
    }

    return { x: 0, y: 0 };
  };

  const onStop = (tableId: string, dragData: DraggableData) => {
    if (!scadaId) return;

    const ref = refs.current[tableId]?.current;
    if (!ref) return;

    const { x, y } = getLabelXY(ref, dragData);
    scadaStore.updateFreeTextTableXY(scadaId, tableId, x, y);

    const updatedTables = (freeTextTagTables ?? []).map(t => (t.id === tableId ? { ...t, x, y } : t));
    updateScadaConfig({ freeTextTagTables: updatedTables });
  };

  const grid: [number, number] = [(parent.width / 100) * gridSize, (parent.height / 100) * gridSize];

  if (!freeTextTagTables?.length || !parent.width || !parent.height) return null;

  return (
    <>
      {freeTextTagTables.map(table => {
        const ref = refs.current[table.id]!;
        const storeTable = scadaStore.scadaMap.get(scadaId!)?.freeTextTagTables?.find(t => t.id === table.id);
        const x = storeTable?.x ?? table.x;
        const y = storeTable?.y ?? table.y;
        const xPx = (x / 100) * parent.width;
        const yPx = (y / 100) * parent.height;
        return (
          <Draggable
            key={table.id}
            nodeRef={ref as RefObject<HTMLElement>}
            bounds="parent"
            grid={grid}
            disabled={!isEditMode}
            position={{ x: xPx, y: yPx }}
            onStop={(_, dragData) => onStop(table.id, dragData)}
          >
            <div
              ref={ref}
              className="free-text-tag-table"
              style={{
                cursor: isEditMode ? "move" : "default",
                pointerEvents: isEditMode ? "auto" : "none",
              }}
            >
              <div
                style={{
                  transform: `scale(${calculateLabelTransform(parent.width, fontSizeScale ?? 1)})`,
                  transformOrigin: "top left",
                }}
              >
                {table.rows.map((row, index) => (
                  <div
                    key={row.id}
                    className="text-tag-table-row"
                    style={{
                      color: row.text.color ?? "#000",
                      fontSize: `${row.text.fontSize ?? 12}px`,
                      paddingBottom: table.useSeparators ? "0.25rem" : undefined,
                      borderBottom:
                        table.useSeparators && index < table.rows.length - 1
                          ? "1px solid rgba(0,0,0,0.2)"
                          : "none",
                    }}
                  >
                    <span className="text-tag-table-text" style={{ maxWidth: table.textColWidth ?? "10rem" }}>
                      {row.text.text}:
                    </span>

                    {row.tag.identifier && (
                      <ScadaTag
                        key={row.tag.identifier}
                        identifier={row.tag.identifier}
                        dataset={dataMap.get(row.tag.identifier)}
                        model={props}
                        scadaId={scadaId!}
                        x={0}
                        y={0}
                        gridSize={gridSize}
                        parent={parent}
                        refTime={props.refTime}
                        bgColor="#f5f5f5"
                        fontSize={row.text.fontSize ?? 12}
                        isRound={row.tag.isRound}
                        roundSize={row.tag.roundSize}
                        highlights={row.tag.highlights}
                        refreshRate={props.refreshRate}
                        inlineMode
                        disableDrag
                        diffOptions={row.tag.diffOptions}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Draggable>
        );
      })}
    </>
  );
};

export default ScadaFreeTextTagTables;
